import type { Observable, Subscription } from 'ladda-observable';

// Creates an Observable object that publishes a constant value once.
export function constObservable<T>(value: T) {
    return {
        subscribe: (publishHandler: (data: T) => void) => {
            publishHandler.apply(null, [value]);
            return {
                unsubscribe: () => undefined,
            };
        },
    };
}

export const mapObservable = <T, U>(mapFn: (value: T) => U, observable: Observable<T>): Observable<U> => ({
    subscribe: (publishHandler, errorHandler) => observable.subscribe(x => publishHandler(mapFn(x)), errorHandler),
});

export const combineObservables = <T extends any[]>(sources: { [K in keyof T]: Observable<T[K]> }): Observable<T> => ({
    subscribe: (publishHandler, errorHandler) => {
        const total = sources.length;

        const results: any[] = new Array(total);
        const hasEmitted: boolean[] = new Array(total).fill(false);
        let readyCount = 0;

        const subscriptions: Subscription[] = [];

        const clearAll = () => subscriptions.forEach(sub => sub.unsubscribe());

        sources.forEach((obs, index) => {
            const sub = obs.subscribe(
                data => {
                    results[index] = data;

                    if (!hasEmitted[index]) {
                        hasEmitted[index] = true;
                        readyCount++;
                    }

                    if (readyCount === total) {
                        publishHandler([...results] as T);
                    }
                },
                error => {
                    errorHandler(error);
                    clearAll();
                }
            );
            subscriptions.push(sub);
        });

        return {
            unsubscribe: () => clearAll(),
        };
    },
});
